/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import {Typography} from '@material-ui/core';
import classnames from 'classnames';
import debounce from 'lodash.debounce';
import {translate} from '@ion/core/src/locale/locale';
import {AccountLink} from '@ion/core/src/types/entities';
import {useAutocompleteSearch} from '../../hooks/useAutocompleteSearch';
import {addItemToTree} from '../../selectiontree/utils/TreeViewUtils';
import {CachedData, CachedItem, FilterItems} from '../../types/types';
import {useStyles} from '../Filter';
import {getSavedFilterFormat} from '../FilterHelpers';
import {TreeClient} from '../GroupFilter';
import {GroupsSearch} from './GroupsSearch';
import {GroupsTree} from './GroupsTree';

export interface GroupsTreeFilterContainerProps extends TreeClient {
  appliedSaveFilter?: string;
  isFilterOpen: boolean;
  account: {
    accountHierarchy: AccountLink[];
    globalGroup: AccountLink;
    globalGroupHierarchy: any;
  };
  onUpdateItemStored?: Function;
  cachedData?: CachedData;
  isMobileView?: boolean;
  onMultiDropdownChecked?: Function;
}

export const GroupsTreeFilterContainer = forwardRef(({
  appliedSaveFilter,
  isFilterOpen,
  treeNodes,
  updateTreeNodes,
  onSelected,
  updateKeys,
  treeKeys,
  nodeKeys,
  account,
  assetLoading,
  onUpdateItemStored,
  cachedData,
  deviceFilterState,
  groupFilterState,
  assetFilterState,
  scFilterProps,
  isMobileView,
  onMultiDropdownChecked,
  selectedGroups
}: GroupsTreeFilterContainerProps, ref) => {
  const classes = useStyles({useDefaultHeight: true, menuContentPadding: '19px 0'});
  const groupsTreeRef = useRef(null);
  const treeNodesRef  = useRef(treeNodes);
  const cachedDataRef = useRef(cachedData);
  const nodeKeysRef   = useRef(nodeKeys);
  const [searchValue, setSearchValue] = useState<string>('');
  const [isSearchableData, setIsSearchableData] = useState(false);
  const [options, setOptions] = useState([]);
  const {
    openSearch,
    closeSearch,
    openFilterSearch
  } = useAutocompleteSearch();
  const {
    includeSCDevice,
    SCDeviceTypes
  } = scFilterProps || {};

  useEffect(function() {
    treeNodesRef.current = treeNodes;
  }, [treeNodes]);

  useEffect(function() {
    cachedDataRef.current = cachedData;
  }, [cachedData]);

  useEffect(function() {
    nodeKeysRef.current = nodeKeys;
  }, [nodeKeys]);

  useEffect(function() {
    if (appliedSaveFilter) {
      const items = getSavedFilterFormat({
        nodeKeys : nodeKeysRef.current,
        groupFilterState,
        assetFilterState,
        deviceFilterState
      });
      const params = {
        target            : items,
        cachedData        : cachedDataRef.current,
        checkRequired     : true,
        isFromSavedFilter : true,
        treeNodes         : null,
        onUpdateItemStored,
        updateTreeNodes,
        addItemToTree,
        updateKeys
      };
      groupsTreeRef?.current?.updateTreeNodesValue(treeNodesRef.current);
      groupsTreeRef?.current?.onAddingItemToTree(params);
    }
  }, [appliedSaveFilter, groupFilterState, assetFilterState, deviceFilterState]);

  /**
   * useeffect to initialize searchable data.
   * On close the data needs to be reset as GCC uses same search class instance.
   */
  useEffect(function() {
    if (isFilterOpen && !isSearchableData) {
      groupFilterInit();
    }
    // This is for clickaway listener call,
    if (!isFilterOpen && isSearchableData) {
      groupsTreeRef?.current?.onClose(updateKeys);
    }
  }, [isFilterOpen]);

  /**
   * useeffect to fetch results and transform into autocomplete list items.
   */
  useEffect(function() {
    const onSearch = async() => {
      const params = {
        searchValue,
        maxResultLength : 100,
        includeAssets   : true,
        deviceFilter    : SCDeviceTypes,
        includeSCDevice
      };
      const options = await groupsTreeRef?.current?.findGroupsSearchResults(params);
      setOptions(options);
    };
    if (searchValue?.length >= 3) {
      onSearch();
    }
  }, [searchValue, groupsTreeRef]);

  /**
   * @groupFilterInit
   * Utility function to initialize the groups when we open the filter.
   * This will handle GCC change as well.
   */
  const groupFilterInit = useCallback(function() {
    groupsTreeRef?.current?.setAccountsToSearch(account?.globalGroupHierarchy ?? [account?.globalGroup]);
    setIsSearchableData(true);
  }, [account, groupsTreeRef]);

  const onSearchChange = useCallback(debounce(function({target: {value}}) {
    setSearchValue(value);
  }, 350), [searchValue]);

  /**
   * @onOptionSelect
   * Function to fetch the selected group from the search field dropdown.
   */
  const onOptionSelect = useCallback(function(event, value) {
    const params = {
      target : value,
      onUpdateItemStored,
      updateTreeNodes,
      addItemToTree,
      updateKeys,
      cachedData,
      treeNodes
    };
    groupsTreeRef?.current?.onAddingItemToTree({
      ...params,
      checkRequired    : true, // this auto checks the selection.
      isFromTreeSearch : true
    });
    setOptions([]);
    setSearchValue('');
  }, [groupsTreeRef, treeNodes, cachedData]);

  /**
   * @onSearchInputChange
   * Function to update tree keys on search input clear
   */
  const onSearchInputChange = useCallback(function(event, value, reason) {
    if (reason === 'clear') {
      groupsTreeRef?.current?.onClose(updateKeys);
      setOptions([]);
      setSearchValue('');
      closeSearch();
    }
  }, [groupsTreeRef]);

  /**
   * @onFilterClose
   * Clean up function to register the checked, expanded and half checked keys
   * of the Treeview.
   */
  const onFilterClose = useCallback(() => {
    setIsSearchableData(false);
    groupsTreeRef?.current?.onClose(updateKeys, treeKeys?.checkedKeys?.length > 0);
    setSearchValue('');
  }, [updateKeys, groupsTreeRef, treeKeys]);

  /**
   * Defines a utility to save expanded nodes and load assets in the tree
   */
  const onAssetLoaderSelected = useCallback((info: any) => {
    groupsTreeRef?.current?.saveExpandedKeys?.(updateKeys);
    onSelected?.(info);
  }, [updateKeys]);

  /**
   * getCheckedKeys and getGroupsForCheckedKeys are passed from GroupsTree component.
   */
  useImperativeHandle(ref, () => ({
    getCheckedKeys          : () => groupsTreeRef?.current?.getCheckedKeys(),
    getGroupsForCheckedKeys : groupsTreeRef?.current?.getGroupsForCheckedKeys,
    onFilterClose           : onFilterClose,
    onAddingItemToTree      : groupsTreeRef?.current?.onAddingItemToTree,
    updateTreeNodesValue    : groupsTreeRef?.current?.updateTreeNodesValue,
    addItem                 : groupsTreeRef?.current?.addItem
  }));

  return (
    <div id='groups-menu-content'>
      <div className={classes.groupAssetsFilterHeaderBar}>
        <Typography classes={{root: classes.groupAssetsfilterContainerHeader}}>
          {translate('common:filters.groupsAssets')}
        </Typography>
        <GroupsSearch classes={classes}
          closeSearch={closeSearch}
          onSearchInputChange={onSearchInputChange}
          onSearchChange={onSearchChange}
          onOptionSelect={onOptionSelect}
          openFilterSearch={openFilterSearch}
          openSearch={openSearch}
          options={options} />
      </div>
      <div className={classnames(classes.menuContentDetails, classes.menuContent, classes.fullViewContent)}>
        <GroupsTree account={account}
          assetFilterState={assetFilterState}
          assetLoading={assetLoading}
          cachedData={cachedData}
          deviceFilterState={deviceFilterState}
          includeSCDevice={includeSCDevice}
          isMobileView={isMobileView}
          nodeKeys={nodeKeys}
          onMultiDropdownChecked={onMultiDropdownChecked}
          onSelected={onAssetLoaderSelected}
          onUpdateItemStored={onUpdateItemStored}
          ref={groupsTreeRef}
          selectedGroups={selectedGroups}
          treeKeys={treeKeys}
          treeNodes={treeNodes}
          updateKeys={updateKeys}
          updateTreeNodes={updateTreeNodes} />
      </div>
    </div>
  );
});
